// Projections of a stored SDI/RDI timeline event into what the wizards, recaps and review pages consume.
import { tiptapDocFromString } from '../rich-editor/plainText';
import type { TiptapDoc } from '../rich-editor/types';
import { DEFAULT_ALIGNER_FIELDS, deriveAlignerFields } from './alignerFields';
import { parseElasticSchemes } from './elastics/persistence';
import type { LegacyElastics } from './elastics/model';
import type { ManufacturerPdfRef, RDIFormData, SDIFormData, SectionToggles } from './types';

export interface ClinicalEventLike {
    payload?: unknown;
}

function payloadOf(event: ClinicalEventLike): Record<string, unknown> | null {
    const payload = event.payload;
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
}

function pickString(obj: Record<string, unknown>, key: string): string {
    const value = obj[key];
    return typeof value === 'string' ? value : '';
}

function pickTiptapDoc(obj: Record<string, unknown>, key: string): TiptapDoc | undefined {
    const value = obj[key];
    if (value && typeof value === 'object' && (value as Record<string, unknown>).type === 'doc') {
        return value as TiptapDoc;
    }
    return undefined;
}

function pickLegacyElastics(fd: Record<string, unknown>): LegacyElastics | undefined {
    const nested = fd.legacyElastics;
    const src = (nested && typeof nested === 'object' ? nested : fd) as Record<string, unknown>;
    const leftElastic = pickString(src, 'leftElastic');
    const rightElastic = pickString(src, 'rightElastic');
    const leftElasticSize = pickString(src, 'leftElasticSize');
    const rightElasticSize = pickString(src, 'rightElasticSize');
    if (!leftElastic && !rightElastic && !leftElasticSize && !rightElasticSize) return undefined;
    return { leftElastic, rightElastic, leftElasticSize, rightElasticSize };
}

type NextAppointmentKey = 'nextAppointmentAligner' | 'nextAppointment';
type FormDataWith<K extends NextAppointmentKey> = Omit<SDIFormData, 'nextAppointmentAligner'> &
    Record<K, string>;

// Legacy events stored before a field existed rehydrate as '' rather than the
// current defaults: editing an old submission must not rewrite what was sent.
function extractFormData<K extends NextAppointmentKey>(
    event: ClinicalEventLike,
    nextAppointmentKey: K
): FormDataWith<K> | null {
    const raw = payloadOf(event)?.formData;
    if (!raw || typeof raw !== 'object') return null;
    const fd = raw as Record<string, unknown>;
    const date = pickString(fd, 'date');
    const patientName = pickString(fd, 'patientName');
    if (!date || !patientName) return null;
    const notesStr = pickString(fd, 'notes');
    const notesDoc = pickTiptapDoc(fd, 'notesDoc') ?? tiptapDocFromString(notesStr);
    return {
        date,
        patientName,
        notes: notesStr,
        notesDoc,
        elasticsSchemes: parseElasticSchemes(fd.elasticsSchemes, fd.elasticsPlacements),
        legacyElastics: pickLegacyElastics(fd),
        numberOfAligners: pickString(fd, 'numberOfAligners'),
        [nextAppointmentKey]: pickString(fd, nextAppointmentKey),
        alignerWear: pickString(fd, 'alignerWear'),
        dmScanFrequency: pickString(fd, 'dmScanFrequency'),
        alignerChangeFrequency: pickString(fd, 'alignerChangeFrequency')
    } as FormDataWith<K>;
}

export function extractSdiFormDataFromEvent(event: ClinicalEventLike): SDIFormData | null {
    return extractFormData(event, 'nextAppointmentAligner');
}

export function extractRdiFormDataFromEvent(event: ClinicalEventLike): RDIFormData | null {
    return extractFormData(event, 'nextAppointment');
}

export const DEFAULT_TOGGLES: SectionToggles = {
    elasticsEnabled: false,
    alignerScheduleEnabled: true,
    alignerFields: DEFAULT_ALIGNER_FIELDS
};

export function extractTogglesFromEvent(event: ClinicalEventLike): SectionToggles {
    const raw = payloadOf(event);
    if (!raw) return DEFAULT_TOGGLES;
    const elasticsEnabled = typeof raw.elasticsEnabled === 'boolean' ? raw.elasticsEnabled : false;
    const alignerScheduleEnabled =
        typeof raw.alignerScheduleEnabled === 'boolean' ? raw.alignerScheduleEnabled : true;
    const alignerFields = deriveAlignerFields(raw.alignerFields, alignerScheduleEnabled);
    return { elasticsEnabled, alignerScheduleEnabled, alignerFields };
}

export function extractManufacturerPdfRef(event: ClinicalEventLike): ManufacturerPdfRef | null {
    const raw = payloadOf(event);
    if (!raw) return null;
    // Drafts keep the ref at the top level, submitted events under files.manufacturerPdf;
    // older submissions carry ids only, so filename/sizeBytes are optional.
    const files = raw.files;
    const ref =
        raw.manufacturerPdf ??
        (files && typeof files === 'object'
            ? (files as Record<string, unknown>).manufacturerPdf
            : undefined);
    if (!ref || typeof ref !== 'object') return null;
    const r = ref as Record<string, unknown>;
    if (typeof r.patientFileId !== 'string' || typeof r.fileId !== 'string') return null;
    return {
        patientFileId: r.patientFileId,
        fileId: r.fileId,
        filename: typeof r.filename === 'string' ? r.filename : 'Manufacturer attachment.pdf',
        sizeBytes: typeof r.sizeBytes === 'number' ? r.sizeBytes : 0
    };
}
